import { useMemo, useState } from "react";
import { EntityFragment } from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Popper } from "@/components/atoms/Popper";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { useScope } from "@/hooks/useScope";
import { Objects } from "@/objects";
import { icons } from "@/objects/@icons";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

export function CommandCenterContextEntityPicker({
  entityIdentifier,
  activeEntityIdentifiers,
  setEntityIdentifier,
}: {
  entityIdentifier?: string;
  activeEntityIdentifiers?: string[];
  setEntityIdentifier?: ((identifier: string) => void) | null;
}) {
  const [isEntityPickerOpen, setIsEntityPickerOpen] = useState(false);
  const { entities, isLoadingUser } = useScope();

  const buttonEntity = useMemo(() => {
    if (!entityIdentifier) return undefined;
    return entities?.find((entity) => entity.identifier === entityIdentifier);
  }, [entityIdentifier, entities]);

  const pickerDisabled = useMemo(() => {
    if (!setEntityIdentifier) return true;
    if (!activeEntityIdentifiers) return false;
    return activeEntityIdentifiers.length <= 1;
  }, [setEntityIdentifier, activeEntityIdentifiers]);

  const handleSelectOption = (entity: EntityFragment) => {
    setEntityIdentifier?.(entity.identifier);
    setIsEntityPickerOpen(false);
  };

  return (
    <>
      <div className="grow" />
      <Popper
        isOpenState={[
          isEntityPickerOpen && !pickerDisabled,
          setIsEntityPickerOpen,
        ]}
        placement="bottom-end"
        anchor={
          <Button
            variant="outline"
            size="sm"
            disabled={pickerDisabled}
            className="pl-1 pr-2"
          >
            {!isLoadingUser && (
              <BoxForEntity
                identifier={buttonEntity?.identifier}
                representation="icon"
                size="sm"
              />
            )}
            <Typography level="body-md">
              <Skeleton loading={isLoadingUser}>
                {buttonEntity?.name || buttonEntity?.identifier || "Entity"}
              </Skeleton>
            </Typography>
            {!pickerDisabled && <icons.chevronDown size="1em" />}
          </Button>
        }
      >
        <NodeList
          {...Objects?.EntityObject?.getReferenceValueOptions()}
          size="md"
          filterOptions={(options) =>
            options.filter((option: any) =>
              activeEntityIdentifiers?.includes(option.identifier),
            )
          }
          onSelectOption={handleSelectOption}
          isMultiselect={false}
        />
      </Popper>
    </>
  );
}
